import { EpiLink } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { Button, SmartLink, Title, useWarrantyListContext } from '@components';
import { useAppSelector } from '@hooks';
import CreateIcon from '@mui/icons-material/Create';
import PolicyIcon from '@mui/icons-material/Policy';
import { Box, Stack } from '@mui/material';
import { getSpecialPage } from '@store/app/app.slice';
import { t } from '@translations';
import { useState } from 'react';
const EXPORT_DATA_URL = '/api/warranty-claims/exportData';

type WarrantyListHeaderProps = {
  title?: string;
  warrantyPolicy?: EpiLink;
};

export const WarrantyListHeader: React.FC<WarrantyListHeaderProps> = ({
  title,
  warrantyPolicy,
}: WarrantyListHeaderProps) => {
  const warrantyCreatePage = useAppSelector(getSpecialPage('warrantyCreatePage'));
  const { filters } = useWarrantyListContext();
  const [isExporting, setIsExporting] = useState(false);

  const savedFormData = localStorage.getItem('warrantyFormData');
  const hasDraft = !!savedFormData;
  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      const response = await fetch(EXPORT_DATA_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(filters),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'WarrantyClaims.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error during file download:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box
      display='flex'
      justifyContent={title ? 'space-between' : 'flex-end'}
      alignItems='center'
      mb={4}
      flexWrap='wrap'
    >
      {title && <Title variant='h2'>{title}</Title>}
      <Stack direction='row' gap={warrantyCreatePage ? 2 : 0} flexWrap='wrap'>
        {warrantyPolicy && (
          <Button
            href={warrantyPolicy.href}
            target={warrantyPolicy.target}
            variant='tertiary'
            startIcon={<PolicyIcon />}
          >
            {warrantyPolicy.text}
          </Button>
        )}
        {warrantyCreatePage && (
          <Button
            href={warrantyCreatePage}
            LinkComponent={SmartLink}
            variant='primary'
            startIcon={<CreateIcon />}
            sx={{
              span: {
                margin: 0,
                gap: 1,
                display: 'flex',
                alignItems: 'center',
              },
            }}
          >
            {hasDraft
              ? t(TextCodes.Warranty.Block.ContinueDraft)
              : t(TextCodes.Warranty.Block.CreateNewClaim)}
          </Button>
        )}
        <Button
          variant='secondary'
          onClick={handleExportToExcel}
          busy={isExporting}
          disabled={isExporting}
        >
          {t(TextCodes.Base.ExportToExcel)}
        </Button>
      </Stack>
    </Box>
  );
};
